import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import type { LibraryItem } from "@/types";
import { Search, PlayCircle, FileText, ExternalLink, ChevronDown, Filter, X, Video, MessageCircle, Send, Loader2, Wrench, BookOpen, ClipboardList, Play, LayoutGrid, FileBarChart, Lightbulb, GraduationCap, BookCheck, Sparkles, FileCode } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useTags, Tag, TAG_GROUP_ORDER, SPES_TAG_GROUP_ORDER } from "@/hooks/useTags";
import { useToolsFilter } from "@/hooks/useToolsFilter";
import ToolsFilterSection from "@/components/library/ToolsFilterSection";
import SearchableFilterSection from "@/components/library/SearchableFilterSection";
import GlobalFilterSearch from "@/components/library/GlobalFilterSearch";
import ResourceSubmissionForm from "@/components/ResourceSubmissionForm";
import ErrorBoundary from "@/components/ErrorBoundary";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface LibraryItemWithTags extends LibraryItem {
  resourceTags?: Tag[];
}

interface ChatCitation {
  id: string;
  title: string;
  description: string;
  slug: string;
  url: string;
  similarity: number;
  citationNumber: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: ChatCitation[];
  additionalContext?: string | null;
  contextSource?: 'lovable' | 'magisterium' | null;
  contextModel?: string | null;
  isLoadingContext?: boolean;
}

const Library: React.FC = () => {
  const location = useLocation();
  const [items, setItems] = useState<LibraryItemWithTags[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterLogic, setFilterLogic] = useState<'all' | 'any'>('any');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [selectedToolIds, setSelectedToolIds] = useState<Set<string>>(new Set());
  const { organization } = useOrganization();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  
  const canonicalUrl = `https://buildwithchrist.ai${location.pathname}`;
  
  // Chat state
  const [chatQuestion, setChatQuestion] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState<'searching' | 'generating' | 'context' | null>(null);
  const chatEndRef = React.useRef<HTMLDivElement>(null);
  
  const { tagsByGroup, loading: tagsLoading, getResourcesWithTags, getOrderedGroups, hasTag } = useTags();
  const { allTools: toolTags, loading: toolsLoading } = useToolsFilter(organization?.id);
  const pendingChatQuestionRef = React.useRef<string | null>(null);

  // Auto-scroll to newest message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [chatMessages, isChatLoading]);

  useEffect(() => {
    if (organization?.id) {
      loadLibrary();
    }
  }, [organization?.id]);

  // Listen for external library search from ChatPane
  useEffect(() => {
    const handleExternalSearch = (event: CustomEvent<{ query: string }>) => {
      const query = event.detail?.query;
      if (query) {
        setSearch(query);
        setIsSearchOpen(true);
      }
    };
    
    window.addEventListener("executeLibrarySearch", handleExternalSearch as EventListener);
    return () => window.removeEventListener("executeLibrarySearch", handleExternalSearch as EventListener);
  }, []);

  // Listen for external library filter from ChatPane
  useEffect(() => {
    const handleExternalFilter = (event: CustomEvent<{ tagName: string }>) => {
      const tagName = event.detail?.tagName;
      if (tagName) {
        // Find tag by name across all groups
        for (const tags of Object.values(tagsByGroup)) {
          const found = tags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
          if (found) {
            setSelectedTags(prev => {
              const newSet = new Set(prev);
              newSet.add(found.id);
              return newSet;
            });
            break;
          }
        }
      }
    };
    
    window.addEventListener("executeLibraryFilter", handleExternalFilter as EventListener);
    return () => window.removeEventListener("executeLibraryFilter", handleExternalFilter as EventListener);
  }, [tagsByGroup]);

  // Listen for external library chat from ChatPane
  useEffect(() => {
    const handleExternalChat = (event: CustomEvent<{ question: string }>) => {
      const question = event.detail?.question;
      if (question) {
        pendingChatQuestionRef.current = question;
        setChatQuestion(question);
      }
    };
    
    window.addEventListener("executeLibraryChat", handleExternalChat as EventListener);
    return () => window.removeEventListener("executeLibraryChat", handleExternalChat as EventListener);
  }, []);

  // Auto-submit pending chat question
  useEffect(() => {
    if (pendingChatQuestionRef.current && chatQuestion === pendingChatQuestionRef.current && !isChatLoading) {
      pendingChatQuestionRef.current = null;
      const timer = setTimeout(() => {
        // Trigger form submit programmatically
        const form = document.querySelector('form[data-library-chat-form]') as HTMLFormElement;
        if (form) {
          form.requestSubmit();
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [chatQuestion, isChatLoading]);

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatQuestion.trim() || isChatLoading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: chatQuestion.trim()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    const question = chatQuestion.trim();
    setChatQuestion("");
    setIsChatLoading(true);
    setLoadingStage('searching');

    try {
      setLoadingStage('generating');
      
      // Fire both requests in parallel for faster loading
      const mainPromise = supabase.functions.invoke('library-chat', {
        body: { 
          question,
          organization_id: organization?.id
        }
      });

      const contextPromise = supabase.functions.invoke('library-chat-context', {
        body: { 
          question,
          organization_id: organization?.id,
          context_provider: 'auto'
        }
      });

      // Wait for main response first
      const { data, error } = await mainPromise;

      if (error) throw error;

      // Handle response
      const answerContent = typeof data.answer === 'object' 
        ? data.answer.fromLibrary 
        : data.answer;
      
      const messageId = crypto.randomUUID();
      const hasMoreContext = data.answer?.hasMoreContext && data.contextProvider !== 'none';
      
      const assistantMessage: ChatMessage = {
        id: messageId,
        role: 'assistant',
        content: answerContent || "Unable to generate an answer.",
        citations: data.citations || [],
        isLoadingContext: hasMoreContext
      };
      
      setChatMessages(prev => [...prev, assistantMessage]);
      setIsChatLoading(false);
      setLoadingStage(null);

      // Check if context is ready (it's been loading in parallel!)
      if (hasMoreContext) {
        setLoadingStage('context');
        try {
          // Context request already started, just await its result
          const { data: contextData } = await contextPromise;

          if (contextData?.additionalContext) {
            setChatMessages(prev => prev.map(msg => 
              msg.id === messageId 
                ? { 
                    ...msg, 
                    additionalContext: contextData.additionalContext,
                    contextSource: contextData.contextSource,
                    contextModel: contextData.contextModel,
                    isLoadingContext: false
                  }
                : msg
            ));
          } else {
            // No additional context, just clear loading state
            setChatMessages(prev => prev.map(msg => 
              msg.id === messageId ? { ...msg, isLoadingContext: false } : msg
            ));
          }
        } catch (contextErr) {
          console.error('Context loading error:', contextErr);
          setChatMessages(prev => prev.map(msg => 
            msg.id === messageId ? { ...msg, isLoadingContext: false } : msg
          ));
        }
        setLoadingStage(null);
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: err.message?.includes('429') || err.status === 429
          ? "Rate limit exceeded. Please try again later."
          : err.message?.includes('402') || err.status === 402
          ? "AI usage limit reached."
          : "Failed to get an answer. Please try again."
      };
      setChatMessages(prev => [...prev, errorMessage]);
      toast.error("Failed to get an answer");
      setIsChatLoading(false);
      setLoadingStage(null);
    }
  };

  const loadLibrary = async () => {
    setLoading(true);
    
    let query = supabase
      .from('library_items')
      .select('*')
      .eq('is_published', true)
      .order('created_at', { ascending: false });
    
    // Filter by organization if available
    if (organization?.id) {
      query = query.eq('organization_id', organization.id);
    }

    const { data, error } = await query;

    if (data && !error) {
      const itemsData = data as LibraryItem[];
      
      // Load tags for all items
      const resourceIds = itemsData.map(item => item.id);
      const tagsMap = await getResourcesWithTags(resourceIds);
      
      // Attach tags to items
      const itemsWithTags = itemsData.map(item => ({
        ...item,
        resourceTags: tagsMap.get(item.id) || []
      }));
      
      setItems(itemsWithTags);
    }
    setLoading(false);
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  const toggleToolTag = (toolId: string) => {
    setSelectedToolIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(toolId)) {
        newSet.delete(toolId);
      } else {
        newSet.add(toolId);
      }
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setSelectedTags(new Set());
    setSelectedToolIds(new Set());
  };

  const hasActiveFilters = selectedTags.size > 0 || selectedToolIds.size > 0;
  const allSelectedTagIds = new Set([...selectedTags, ...selectedToolIds]);

  const filteredItems = items.filter(item => {
    const matchesSearch =
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase());
    
    if (!hasActiveFilters) return matchesSearch;

    const itemTagIds = new Set(item.resourceTags?.map(t => t.id) || []);
    
    if (filterLogic === 'any') {
      // OR logic: match any selected tag (including tool tags)
      const matchesAnyTag = Array.from(allSelectedTagIds).some(tagId => itemTagIds.has(tagId));
      return matchesSearch && matchesAnyTag;
    } else {
      // AND logic: match all selected tags (including tool tags)
      const matchesAllTags = Array.from(allSelectedTagIds).every(tagId => itemTagIds.has(tagId));
      return matchesSearch && matchesAllTags;
    }
  });

  const getIcon = (type: string) => {
    switch (type) {
      case 'video':
      case 'show-and-tell':
        return <PlayCircle size={24} />;
      case 'tool':
        return <Wrench size={24} />;
      case 'tutorial':
        return <GraduationCap size={24} />;
      case 'guide':
      case 'playbook':
        return <BookOpen size={24} />;
      case 'template':
      case 'checklist':
        return <ClipboardList size={24} />;
      case 'demo':
        return <Play size={24} />;
      case 'framework':
        return <LayoutGrid size={24} />;
      case 'case-study':
      case 'research':
        return <FileBarChart size={24} />;
      case 'tips':
        return <Lightbulb size={24} />;
      case 'story':
        return <BookCheck size={24} />;
      default:
        return <FileText size={24} />;
    }
  };

  // Get the tag name from ID for display
  const getTagById = (tagId: string): Tag | undefined => {
    for (const tags of Object.values(tagsByGroup)) {
      const found = tags.find(t => t.id === tagId);
      if (found) return found;
    }
    return undefined;
  };

  // Check if item is a Show & Tell
  const isShowAndTell = (item: LibraryItemWithTags): boolean => {
    return hasTag(item.resourceTags, "Show & Tell");
  };

  // Helper for Show & Tell video icon
  const showVideoIcon = (tag: Tag) => tag.name === "Show & Tell";

  const SidebarContent = () => {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">Filters</h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-xs"
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Global Filter Search */}
          <GlobalFilterSearch
            tagsByGroup={tagsByGroup}
            toolTags={toolTags}
            selectedTagIds={selectedTags}
            selectedToolIds={selectedToolIds}
            onToggleTag={toggleTag}
            onToggleTool={toggleToolTag}
          />

          {/* Filter Logic Toggle */}
          {hasActiveFilters && (
            <div className="border border-border rounded-lg p-3 mb-4">
              <p className="text-xs text-muted-foreground mb-2">Show items that match:</p>
              <div className="flex gap-2">
                <Button
                  variant={filterLogic === 'any' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterLogic('any')}
                  className="flex-1 text-xs"
                >
                  Any filter
                </Button>
                <Button
                  variant={filterLogic === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterLogic('all')}
                  className="flex-1 text-xs"
                >
                  All filters
                </Button>
              </div>
            </div>
          )}

          {/* Dynamic filter sections from database */}
          {(tagsLoading || toolsLoading) ? (
            <p className="text-sm text-muted-foreground">Loading filters...</p>
          ) : (
            <>
              {/* Render filter sections in order - use SPES-specific categories for SPES org */}
              {(organization?.slug === 'spes' ? SPES_TAG_GROUP_ORDER : TAG_GROUP_ORDER).map(group => {
                if (group === 'Tools') {
                  return (
                    <ToolsFilterSection 
                      key={group}
                      selectedToolIds={selectedToolIds}
                      onToggle={toggleToolTag}
                    />
                  );
                }
                return (
                  <SearchableFilterSection
                    key={group}
                    title={group}
                    tags={tagsByGroup[group] || []}
                    selectedTagIds={selectedTags}
                    onToggle={toggleTag}
                    maxVisible={6}
                    showVideoIcon={group === 'Content Type' || group === 'Document Type' ? showVideoIcon : undefined}
                  />
                );
              })}
            </>
          )}
        </div>

      </div>
    );
  };

  return (
    <div className="min-h-screen flex">
      <Helmet>
        <title>{organization?.slug === 'spes' ? 'Catholic Evangelization Repository' : 'Resource Library'} | Build With Christ</title>
        <meta name="description" content="Guides, templates, and stories to help you build AI for good. Filter by type or search for specific tools." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 border-r border-border bg-card/30 p-6 sticky top-0 h-screen">
        <ScrollArea className="h-[calc(100vh-48px)]">
          <SidebarContent />
        </ScrollArea>
      </aside>

      {/* Main Content */}
      <div className="flex-1 py-8 sm:py-12 px-3 sm:px-4 max-w-7xl mx-auto w-full">
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 font-display">
            {organization?.slug === 'spes' ? 'Catholic Evangelization Repository' : 'Resource Library'}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl">
            {organization?.slug === 'spes' 
              ? 'Resources, formation materials, and best practices for diocesan leadership and evangelization.'
              : 'Guides, templates, and stories to help you build AI for good. Filter by type or search for specific tools.'}
          </p>
        </div>

        {/* Chat With the Library Section */}
        <Card className="mb-6 sm:mb-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 flex flex-col">
          {/* Header */}
          <div className="flex items-start gap-3 p-3 sm:p-4 border-b border-border/50">
            <div className="p-2 rounded-lg bg-primary/20 text-primary shrink-0">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-base sm:text-lg">Chat With the Library</h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Ask questions and get answers sourced from the Resource Library.
              </p>
            </div>
          </div>

          {/* Messages Area */}
          {(chatMessages.length > 0 || isChatLoading) && (
            <ScrollArea className="flex-1 h-64 sm:h-80 p-3 sm:p-4">
              <div className="space-y-3 sm:space-y-4">
                {chatMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                  >
                    <div
                      className={`max-w-[90%] sm:max-w-[85%] rounded-lg p-2.5 sm:p-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background/60 border border-border'
                      }`}
                    >
                      {message.role === 'user' ? (
                        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                      ) : (
                        <div className="space-y-2">
                          {/* Primary Library Response */}
                          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                            <BookOpen className="h-3.5 w-3.5" />
                            From the Library
                          </div>
                          <div className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                          
                          {/* Loading Context Indicator */}
                          {message.isLoadingContext && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-border/50">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading additional context...
                            </div>
                          )}
                          
                          {/* Additional Context */}
                          {message.additionalContext && (
                            <div className="pt-2 border-t border-border/50">
                              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
                                {message.contextSource === 'magisterium' ? (
                                  <>
                                    <BookOpen className="h-3.5 w-3.5 text-amber-600" />
                                    Additional Context from Magisterium
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="h-3.5 w-3.5 text-blue-500" />
                                    Additional Context
                                  </>
                                )}
                              </div>
                              <div className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 text-muted-foreground">
                                <ReactMarkdown>{message.additionalContext}</ReactMarkdown>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Citations for assistant messages */}
                      {message.role === 'assistant' && message.citations && message.citations.length > 0 && (
                        <div className="mt-2 sm:mt-3 pt-2 border-t border-border/50">
                          <p className="text-xs font-medium text-muted-foreground mb-1.5 sm:mb-2">Sources:</p>
                          <div className="flex flex-wrap gap-1 sm:gap-1.5">
                            {message.citations.map((citation) => (
                              <Link
                                key={citation.id}
                                to={`/library/${citation.slug || citation.id}`}
                                className="inline-flex items-center gap-1 text-xs px-1.5 sm:px-2 py-0.5 bg-secondary/20 hover:bg-secondary/30 rounded text-secondary transition-colors"
                              >
                                <span className="hidden sm:inline">[{citation.citationNumber}]</span>
                                <span className="truncate max-w-[120px] sm:max-w-[200px]">{citation.title}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Loading indicator with progressive stages */}
                {isChatLoading && (
                  <div className="flex justify-start animate-fade-in">
                    <div className="bg-background/60 border border-border rounded-lg p-2.5 sm:p-3">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">
                          {loadingStage === 'searching' && 'Searching the library...'}
                          {loadingStage === 'generating' && 'Generating answer...'}
                          {!loadingStage && 'Processing...'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>
          )}
          
          {/* Input Bar - Fixed at bottom */}
          <div className="p-3 sm:p-4 border-t border-border/50">
            <form onSubmit={handleChatSubmit} className="flex gap-2" data-library-chat-form>
              <Input
                type="text"
                placeholder="Ask the Library anything…"
                className="flex-1 bg-background/80 text-sm sm:text-base"
                value={chatQuestion}
                onChange={(e) => setChatQuestion(e.target.value)}
                disabled={isChatLoading}
              />
              <Button type="submit" disabled={isChatLoading || !chatQuestion.trim()} size="default" className="shrink-0">
                {isChatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          </div>
        </Card>

        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Button 
            variant={isSearchOpen ? "default" : "outline"} 
            className="flex-1 gap-2 h-11 sm:h-10"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
          >
            <Search className="h-4 w-4" />
            Search Resources
          </Button>
          <Button 
            variant={isSubmitOpen ? "default" : "outline"} 
            className="flex-1 gap-2 h-11 sm:h-10"
            onClick={() => setIsSubmitOpen(!isSubmitOpen)}
          >
            <ExternalLink className="h-4 w-4" />
            Submit a Resource
          </Button>
        </div>

        {/* Collapsible Search Panel */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isSearchOpen ? 'max-h-[500px] opacity-100 mb-4 sm:mb-6' : 'max-h-0 opacity-0'}`}>
          <div className="p-3 sm:p-4 bg-card border border-border rounded-lg">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                <Input
                  type="text"
                  placeholder="Search resources..."
                  className="pl-10 w-full"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              {/* Mobile Filter Sheet */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden flex items-center justify-center gap-2 w-full sm:w-auto">
                    <Filter size={20} />
                    <span>Filters</span>
                    {hasActiveFilters && (
                      <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                        {selectedTags.size}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0">
                  <SheetHeader className="p-6 pb-0">
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <ScrollArea className="h-[calc(100vh-80px)] px-6">
                    <SidebarContent />
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Collapsible Submit Panel */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isSubmitOpen ? 'max-h-[1000px] opacity-100 mb-4 sm:mb-6' : 'max-h-0 opacity-0'}`}>
          <ResourceSubmissionForm />
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-6">
            {Array.from(selectedTags).map((tagId) => {
              const tag = getTagById(tagId);
              return tag ? (
                <Button
                  key={tagId}
                  variant="secondary"
                  size="sm"
                  onClick={() => toggleTag(tagId)}
                  className="gap-2"
                >
                  {tag.name}
                  <X size={14} />
                </Button>
              ) : null;
            })}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading resources...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No resources found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredItems.map(item => {
              const showAndTell = isShowAndTell(item);
              
              return (
                <Link
                  key={item.id}
                  to={`/library/${item.slug || item.id}`}
                  className={`bg-card border rounded-xl p-6 hover:border-primary transition-colors group cursor-pointer block relative ${
                    showAndTell 
                      ? 'border-amber-500/40 bg-amber-500/5 hover:border-amber-500' 
                      : 'border-border'
                  }`}
                >
                  {/* Show & Tell Badge */}
                  {showAndTell && (
                    <Badge 
                      className="absolute -top-2 -right-2 bg-amber-500 text-amber-950 hover:bg-amber-400"
                    >
                      🎥 Show & Tell
                    </Badge>
                  )}
                  
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-2 rounded-lg ${showAndTell ? 'bg-amber-500/20 text-amber-500' : 'bg-primary/10 text-primary'}`}>
                      {getIcon(item.type)}
                    </div>
                    <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full uppercase">
                      {item.type}
                    </span>
                  </div>
                  {(() => {
                    // Check for parentheses format: "Name (Subtitle)"
                    const parenMatch = item.title.match(/^([^(]+)\s*\(([^)]+)\)$/);
                    const colonMatch = !parenMatch ? item.title.match(/^([^:]+):\s*(.+)$/) : null;
                    const mainTitle = parenMatch ? parenMatch[1].trim() : colonMatch ? colonMatch[1].trim() : item.title;
                    const subtitle = parenMatch ? parenMatch[2].trim() : colonMatch ? colonMatch[2].trim() : null;
                    return (
                      <>
                        <h3 className={`font-bold text-lg mb-1 transition-colors ${showAndTell ? 'group-hover:text-amber-500' : 'group-hover:text-primary'}`}>
                          {mainTitle}
                        </h3>
                        {subtitle && (
                          <p className="text-sm text-muted-foreground mb-2">{subtitle}</p>
                        )}
                      </>
                    );
                  })()}
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.resourceTags?.slice(0, 5).map((tag) => (
                      <span 
                        key={tag.id} 
                        className={`text-xs px-2 py-1 rounded ${
                          tag.name === "Show & Tell" 
                            ? 'bg-amber-500/20 text-amber-600' 
                            : 'bg-secondary/10 text-secondary'
                        }`}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                  <span className={`inline-flex items-center gap-2 text-sm hover:underline ${showAndTell ? 'text-amber-500' : 'text-primary'}`}>
                    View Details <ExternalLink size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;
